import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Optional,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { NgClassInterface } from '@ngx-simple/core/types';
import { Subject } from 'rxjs';
import { CalendarUserEvent } from './calendar-body.component';
import { DateRange } from './date-selection-model';
import { SimMonthViewComponent } from './month-view.component';
import { SimMultiYearViewComponent } from './multi-year-view.component';
import { SimTimeViewComponent } from './time-view.component';
import { SimYearViewComponent } from './year-view.component';

export type SimCalendarView = 'month' | 'year' | 'multi-year' | 'date' | 'week';

@Component({
  selector: 'sim-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sim-calendar'
  }
})
export class SimCalendarComponent<D> implements OnChanges, AfterContentInit {
  @Input()
  get selected(): DateRange<D> | D | null {
    return this._selected;
  }
  set selected(value: DateRange<D> | D | null) {
    if (value instanceof DateRange) {
      this._selected = value;
    } else {
      this._selected = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
    }
  }

  /** 最小选择日期 */
  @Input()
  get minDate(): D | null {
    return this._minDate;
  }
  set minDate(value: D | null) {
    this._minDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }

  /** 最大选择日期 */
  @Input()
  get maxDate(): D | null {
    return this._maxDate;
  }
  set maxDate(value: D | null) {
    this._maxDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }

  get currentView(): SimCalendarView {
    return this._currentView;
  }
  set currentView(value: SimCalendarView) {
    this._currentView = value;
    // this._moveFocusOnNextTick = true;
    this._changeDetectorRef.markForCheck();
  }

  get activeDate(): D {
    return this._activeDate;
  }
  set activeDate(value: D) {
    this._activeDate = this._dateAdapter.clampDate(value, this.minDate, this.maxDate);
    this.stateChanges.next();

    this._changeDetectorRef.markForCheck();
  }

  /** 表示日历开始的时期(月或年)的日期 */
  @Input()
  get startAt(): D | null {
    return this._startAt;
  }
  set startAt(value: D | null) {
    this._startAt = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }

  @Input() simCalendarFooter: TemplateRef<void>;

  /** 日历是否应该在月或年视图中启动 */
  @Input() startView: SimCalendarView = 'month';
  /**
   * 选择面板 决定 startView 值
   * year 选择多年面板 => multi-year
   * month 选择月份面板 => multi-year | year
   * week 选择周面板 => multi-year | year | month
   * date 选择日期面板 => multi-year | year | month
   * date-time 选择日期时间面板 => multi-year | year | month | date
   */
  @Input() picker: 'year' | 'month' | 'week' | 'date' | 'datetime' = 'date';

  /** 用于过滤哪些日期可选的方法 */
  @Input() dateFilter: (date: D) => boolean;

  /** 可用于将自定义CSS类添加到日期的函数 */
  @Input() dateClass: (date: D) => NgClassInterface;

  /** 比较范围的开始 */
  @Input() comparisonStart: D | null;

  /** 比较范围的结束 */
  @Input() comparisonEnd: D | null;

  @Input() enablePrev: boolean;
  @Input() enableNext: boolean;

  @Output() readonly selectedChange: EventEmitter<D> = new EventEmitter();
  /** 切换header button发出 */
  @Output() readonly toggleActiveDateChange: EventEmitter<[D, SimCalendarView, number]> = new EventEmitter();
  /** 选择任何日期时发出 */
  @Output() readonly userSelection: EventEmitter<CalendarUserEvent<D | null>> = new EventEmitter();
  /**
   * 退出在多年视图中选择的年份。 这并不意味着所选日期会发生变化。
   */
  @Output() readonly yearSelected: EventEmitter<D> = new EventEmitter<D>();

  /**
   * 退出在年视图中选择的月份。 这并不意味着所选日期会发生变化。
   */
  @Output() readonly monthSelected: EventEmitter<D> = new EventEmitter<D>();

  /** 引用当前月份视图组件 */
  @ViewChild(SimTimeViewComponent) timeView: SimTimeViewComponent<D>;

  /** 引用当前月份视图组件 */
  @ViewChild(SimMonthViewComponent) monthView: SimMonthViewComponent<D>;

  /** 引用当前年份视图组件 */
  @ViewChild(SimYearViewComponent) yearView: SimYearViewComponent<D>;

  /** 引用多年份视图组件 */
  @ViewChild(SimMultiYearViewComponent) multiYearView: SimMultiYearViewComponent<D>;

  stateChanges = new Subject<void>();

  private _startAt: D | null;
  private _currentView: SimCalendarView;

  private _selected: DateRange<D> | D | null;
  private _activeDate: D;
  private _minDate: D | null;
  private _maxDate: D | null;

  constructor(private _changeDetectorRef: ChangeDetectorRef, @Optional() private _dateAdapter: SimDateAdapter<D>) {}

  ngOnChanges(changes: SimpleChanges): void {
    // 只有在 日期时间这个面板才能改变外部传来的视图
    if (changes['startView'] && this.picker === 'datetime') {
      this._currentView = this.startView;
    }
  }

  ngAfterContentInit() {
    this.activeDate = this.startAt || this._dateAdapter.today();

    // 赋值给私有属性，因为我们不想把焦点移到init上
    this._currentView = this.startView;
  }

  focusActiveCell() {}

  _yearSelectedInMultiYearView(normalizedYear: D) {
    this.yearSelected.emit(normalizedYear);
  }

  _monthSelectedInYearView(normalizedYear: D) {
    this.monthSelected.emit(normalizedYear);
  }

  _goToDateInView($event: D, view: SimCalendarView) {
    this.currentView = view;
    this.activeDate = $event;
  }

  updateTodaysDate() {
    const currentView = this.currentView;
    let view: SimMonthViewComponent<D> | SimYearViewComponent<D> | SimMultiYearViewComponent<D> | SimTimeViewComponent<D>;

    switch (currentView) {
      case 'month':
        view = this.monthView;
        break;
      case 'year':
        view = this.yearView;
        break;
      case 'multi-year':
        view = this.multiYearView;
        break;
      case 'date':
        view = this.timeView;
        break;
      default:
        break;
    }

    view._init();
  }

  _dateSelected($event: CalendarUserEvent<D | null>) {
    const date = $event.value;

    if (this.selected instanceof DateRange || (date && !this._dateAdapter.sameDate(date, this.selected))) {
      this.selectedChange.emit(date);
    }

    this.userSelection.emit($event);
  }
}
